import { useQuery } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';
import type {
  PayrollHistoryResponse,
  PayrollResponse,
  PayrollRunDetailResponse,
} from './contractsMirror.js';
import type { PayrollSearch } from './searchParams.js';

/**
 * TanStack Query hooks for the COARSE payroll endpoints (the strangler-fig + coarse-endpoint proof,
 * CARVE-07). The SERVER computes the per-employee gross via the golden-locked payroll-calc.ts (22-14);
 * the SPA only renders the returned response.
 *
 * THE GUARDRAIL (D-03 / T-22-payroll): every `grossPay` value in the response is the SERVER-COMPUTED
 * golden number. These hooks (and the views they feed) NEVER recompute the OT / lunch / ÷52 math — the
 * gross-pay formula lives ONLY on the server.
 *
 * Every request goes through `apiFetch` (the only fetch path — `credentials:'include'` +
 * `X-Tenant-Subdomain` baked in, single-flight 401 refresh). The tenant is derived server-side from
 * the auth context; the SPA NEVER sends a tenant id param (threat T-22-tenant).
 */

/** Build the stable query key for a pay period (one cache entry per week). */
export function payrollQueryKey(search: PayrollSearch): readonly unknown[] {
  return ['payroll', search.week ?? null] as const;
}

/** Build the coarse-endpoint request path with the active period (no tenant param, read-only). */
export function payrollRequestPath(search: PayrollSearch): string {
  const params = new URLSearchParams();
  if (search.week) params.set('week', search.week);
  const qs = params.toString();
  return qs ? `/api/v1/payroll?${qs}` : '/api/v1/payroll';
}

/** The Payroll Center query — the per-employee computed-gross review grid for the period. */
export function usePayrollQuery(search: PayrollSearch) {
  return useQuery<PayrollResponse>({
    queryKey: payrollQueryKey(search),
    queryFn: () => apiFetchJson<PayrollResponse>(payrollRequestPath(search)),
  });
}

/** The Payroll History query — the locked-run list for the tenant. */
export function usePayrollHistoryQuery() {
  return useQuery<PayrollHistoryResponse>({
    queryKey: ['payroll', 'history'] as const,
    queryFn: () => apiFetchJson<PayrollHistoryResponse>('/api/v1/payroll/history'),
  });
}

/** The Run Detail query — the locked, immutable snapshot for one run. */
export function usePayrollRunQuery(runId: number) {
  return useQuery<PayrollRunDetailResponse>({
    queryKey: ['payroll', 'run', runId] as const,
    queryFn: () =>
      apiFetchJson<PayrollRunDetailResponse>(`/api/v1/payroll/runs/${runId}`),
    enabled: Number.isFinite(runId) && runId > 0,
  });
}
